import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function JournalMasonryConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    currentPage,
    totalPages
  } = usePosts();
  if (loading) {
    return <div className="columns-1 md:columns-2 lg:columns-3 gap-8">
        {[...Array(6)].map((_, i) => <div key={i} data-index={i} className="mb-8 break-inside-avoid">
            <div className="aspect-[16/10] bg-muted/30 animate-pulse mb-4" />
            <div className="h-4 w-24 bg-muted/30 animate-pulse mb-3" />
            <div className="h-6 w-full bg-muted/30 animate-pulse mb-2" />
            <div className="h-4 w-3/4 bg-muted/30 animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-16">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          Unable to load awareness resources
        </p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-16">
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
          No resources available
        </p>
      </div>;
  }
  const pullQuoteIndex = 5;
  const pullQuote = {
    text: "Awareness is the first step toward change. By keeping these issues fresh in our minds, we build a foundation for a more compassionate world.",
    attribution: "FOREVER FRESH · MISSION TEAM"
  };
  return <>
      <div className="columns-1 md:columns-2 lg:columns-3 gap-8 [column-fill:_balance]">
        {posts.map((post, i) => {
        const isLargeFeature = i === 0 || i % 7 === 0;
        const isTextOnly = i === 3 || i > 0 && i % 11 === 0;
        const showCategory = post.categories && post.categories.length > 0;
        const categoryName = showCategory ? post.categories![0].name : "AWARENESS";
        const dateStr = post.date ? new Date(post.date).toLocaleDateString("en-US", {
          day: "2-digit",
          month: "long",
          year: "numeric"
        }).toUpperCase() : "";
        return <article key={post.id} data-index={i} className="mb-8 break-inside-avoid group">
              {!isTextOnly && post.featuredImage && <Link to={post.link || "#"} className="block mb-6 overflow-hidden rounded-lg shadow-md">
                  <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className={isLargeFeature ? "w-full aspect-[3/4] object-cover transition-all duration-700" : "w-full aspect-[16/10] object-cover transition-all duration-700"} />
                </Link>}

              <div className="space-y-3">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{
                color: "var(--primary)"
              }}>
                    {categoryName}
                  </span>
                  {dateStr && <>
                      <span className="font-mono text-[10px] text-muted-foreground">·</span>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                        {dateStr}
                      </span>
                    </>}
                </div>

                <Link to={post.link || "#"} className="block">
                  <h3 className={isTextOnly ? "font-serif font-default text-3xl md:text-4xl leading-[1.1] tracking-tight text-foreground group-hover:underline decoration-1 underline-offset-[6px] transition-all" : isLargeFeature ? "font-serif font-default text-2xl md:text-3xl leading-[1.15] tracking-tight text-foreground group-hover:underline decoration-1 underline-offset-[6px] transition-all" : "font-serif font-default text-xl md:text-[22px] leading-[1.2] tracking-tight text-foreground group-hover:underline decoration-1 underline-offset-[6px] transition-all"} style={{
                textDecorationColor: "var(--primary)"
              }}>
                    {post.title}
                  </h3>
                </Link>

                {post.excerpt && <div className="font-sans text-[15px] leading-relaxed text-muted-foreground line-clamp-3" dangerouslySetInnerHTML={{
              __html: post.excerpt
            }} />}

                {isTextOnly && post.author && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground pt-2">
                    — BY {post.author.name?.toUpperCase()}
                  </p>}

                <Link to={post.link || "#"} className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.25em] text-foreground hover:gap-3 transition-all pt-2">
                  <span style={{
                color: "var(--primary)"
              }}>READ {isLargeFeature ? "REPORT" : "UPDATE"}</span>
                  <ArrowRight className="w-3 h-3" style={{
                color: "var(--primary)"
              }} />
                </Link>
              </div>

              {i === pullQuoteIndex && <div className="my-12 py-10 border-t border-b border-border/40">
                  <blockquote className="font-serif font-default italic text-2xl md:text-3xl leading-[1.25] text-foreground tracking-tight">
                    &ldquo;{pullQuote.text}&rdquo;
                  </blockquote>
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-6">
                    — {pullQuote.attribution}
                  </p>
                </div>}
            </article>;
      })}
      </div>

      {(hasPrev || hasNext) && <div className="mt-20 pt-10 border-t border-border/40">
          <div className="flex items-center justify-center gap-6 font-mono text-[11px] uppercase tracking-[0.25em]">
            <Button variant="ghost" onClick={prevPage} disabled={!hasPrev} className="font-mono text-[11px] uppercase tracking-[0.25em] rounded-md hover:bg-transparent hover:text-foreground disabled:opacity-30 px-0">
              <span style={{
            color: hasPrev ? "var(--primary)" : "inherit"
          }}>NEWER</span>
            </Button>

            <span className="text-muted-foreground">·</span>

            <div className="flex items-center gap-3">
              {[...Array(Math.min(totalPages, 5))].map((_, i) => {
            const pageNum = i + 1;
            const isActive = pageNum === currentPage;
            return <span key={pageNum} data-index={i} className={isActive ? "font-mono text-[11px]" : "font-mono text-[11px] text-muted-foreground"} style={isActive ? {
              color: "var(--primary)"
            } : {}}>
                    {String(pageNum).padStart(2, "0")}
                  </span>;
          })}
            </div>

            <span className="text-muted-foreground">·</span>

            <Button variant="ghost" onClick={nextPage} disabled={!hasNext} className="font-mono text-[11px] uppercase tracking-[0.25em] rounded-md hover:bg-transparent hover:text-foreground disabled:opacity-30 px-0">
              <span style={{
            color: hasNext ? "var(--primary)" : "inherit"
          }}>OLDER</span>
            </Button>
          </div>
        </div>}
    </>;
}
export default function Awareness() {
  const wp_query_params = {
    post_type: "post",
    per_page: 9,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="5252" id="animalawarenesslibrary" className="bg-background text-foreground py-24 md:py-32 px-6 md:px-12 lg:px-20">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20">
          <div className="lg:col-span-7">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] mb-8" style={{
            color: "var(--primary)"
          }}>
              FOREVER FRESH · AWARENESS LIBRARY
            </p>
            <h1 className="font-serif font-default text-foreground leading-[0.95] tracking-tight" style={{
            fontSize: "clamp(4rem, 9vw, 9rem)"
          }}>
              Keeping Issues
              <br />
              <span className="italic">Forever Fresh.</span>
            </h1>
          </div>

          <div className="lg:col-span-5 lg:pt-8">
            <p className="font-sans text-lg leading-relaxed text-muted-foreground">
              Explore our curated library of awareness resources. We break down complex humanitarian and environmental issues to help you stay informed and take meaningful action.
            </p>
          </div>
        </div>

        <div className="relative flex items-center mb-20">
          <div className="flex-1 h-px" style={{
          backgroundColor: "var(--primary)",
          opacity: 0.5
        }} />
          <span className="px-6 font-mono text-[10px] uppercase tracking-[0.35em]" style={{
          color: "var(--primary)"
        }}>
            — CURRENT CAUSES —
          </span>
          <div className="flex-1 h-px" style={{
          backgroundColor: "var(--primary)",
          opacity: 0.5
        }} />
        </div>

        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 9,
        order: "desc",
        orderby: "date"
      }}>
          <JournalMasonryConsumer />
        </PostsProvider>
      </div>
    </section>;
}